import { chain, contains, head, mergeDeepLeft, omit, pipe, prop, values } from 'ramda';
import { captureException } from '@sentry/react';
import { MediaType } from '../../../constants/MediaType';
import { MainSource } from '../../../constants/Source';
import { WixMediaRoot } from '../../../constants/WixMediaRoot';
import { fedopsLogger } from '../../../services/loggers/fedopsLogger';
import { TMMGRAdapterSettings } from '../../hoc/withMMGRSettings/withMMGRSettings.types';
import { isMediaTypeEnabled } from '../../mediaType';
import { getAdapterWithAdapterSettings } from '../getAdapter';
import { IMediaManagerSDKRequestOptions, MediaManagerSDKFile, RecentSearch } from '../index';
import { getIdOfFirstItem, getSubFoldersOfFirstItem, sortObjectByKeys } from './utils';
import { CustomMonitoringTag, FedOpsInteraction } from '../../../constants/FedOpsInteraction';

interface IServerFilesResponse {
  data: MediaManagerSDKFile[];
}

interface MultiSearchSourceOptions {
  source: MainSource;
  media_type?: string;
  page_size?: number;
  guid?: string;
  language?: string;
  folderId?: string;
  country?: string;
  tags?: string;
  semantic?: boolean;
  namespace?: string;
}

export class MultiSearchSource {
  settings: any;
  items: {
    search(
      folderId: string,
      query: string,
      options: IMediaManagerSDKRequestOptions,
    ): Promise<{ data: MediaManagerSDKFile[] }>;
    getRecentSearches: () => RecentSearch[];
  };
  recentSearches: RecentSearch[];

  constructor(settings: TMMGRAdapterSettings) {
    const { wixMediaSettings, isSecureContext, tabsList, country } = settings;
    const { hideShutterstock, hideUnsplash } = wixMediaSettings;
    const { namespace } = settings.siteFilesSettings;

    const { publicMediaRoot, language } = wixMediaSettings;
    const defaultOption = {
      page_size: 10, // maximum items per row in search results plus one item. We request at least 1 item more then we can show to easily figure out if need to show "Show All" button
    };

    const wixMediaAdapter = getAdapterWithAdapterSettings(MainSource.MediaFromWix, wixMediaSettings);
    const nttAdapter = getAdapterWithAdapterSettings(MainSource.NTT, wixMediaSettings);

    let publicMediaCategories = [];

    const finalSourcesOrder = ['privateFolders', 'privateFiles', 'trashFolders', 'trashFiles', 'visitorFiles'];

    this.items = {
      search: async (folderId, query, options) => {
        try {
          const { mediaTypes } = options;
          const newOptions = pipe(
            omit('mediaTypes'),
            mergeDeepLeft(defaultOption),
            mergeDeepLeft(wixMediaSettings.restrictContent ? { filters: { restrictContent: true } } : {}),
            mergeDeepLeft(wixMediaSettings.restrictK12 ? { filters: { restrictK12: true } } : {}),
          )(options);

          // Do not search in FFW and ShutterStock when context is secure
          const shouldLoadFreeFromFix =
            contains(MainSource.MediaFromWix, tabsList) &&
            isMediaTypeEnabled(MediaType.Picture, mediaTypes) &&
            !isSecureContext;

          const shouldLoadShutterstockImages =
            !hideShutterstock &&
            contains(MainSource.Shutterstock, tabsList) &&
            isMediaTypeEnabled(MediaType.Picture, mediaTypes) &&
            !isSecureContext;
          const shouldLoadShutterstockVideos =
            !hideShutterstock &&
            contains(MainSource.ShutterstockVideo, tabsList) &&
            isMediaTypeEnabled(MediaType.Video, mediaTypes) &&
            !isSecureContext;

          const shouldLoadUnsplash = !hideUnsplash && contains(MainSource.Unsplash, tabsList) && !isSecureContext;
          const shouldLoadNTT = Boolean(window.isNTT);

          const searchableSources: MultiSearchSourceOptions[] = [
            { source: MainSource.SiteFiles, ...defaultOption, ...(namespace ? { ns: namespace } : {}) },
            // trash and visitorUploads should be included by default
          ];

          const getCategoriesMultiSearchPayload = (categories) =>
            Object.keys(categories).map((categoryId) => ({
              mediaType: categories[categoryId].mediaType,
              id: categories[categoryId].subFolders[0].subFolders[0].id,
            }));

          await (shouldLoadFreeFromFix
            ? wixMediaAdapter.folders
                .tree(publicMediaRoot, newOptions) // first we get types
                .then(({ data }) => ({
                  firstCategoryGuid: pipe(getSubFoldersOfFirstItem, getSubFoldersOfFirstItem, getIdOfFirstItem)(data),
                  mediaType: head(data).mediaType,
                  categories: data,
                }))
                .then(({ firstCategoryGuid, mediaType, categories }) => {
                  publicMediaCategories = categories;
                  searchableSources.push(
                    ...getCategoriesMultiSearchPayload(categories).map<MultiSearchSourceOptions>(
                      ({ mediaType: media_type, id: guid }) => ({
                        source: MainSource.MediaFromWix,
                        guid,
                        media_type,
                        language,
                        country,
                        tags: 'free',
                      }),
                    ),
                  );
                })
            : Promise.resolve());

          await (shouldLoadNTT
            ? nttAdapter.folders
                .list(WixMediaRoot.Ntt, newOptions) // first we get types
                .then(({ data: [{ id: firstCategoryGuid }] }) =>
                  // then we get categories
                  nttAdapter.folders.list(firstCategoryGuid, newOptions),
                )
                .then(({ data: [{ id: firstSubcategoryGuid, mediaType }] }) => {
                  searchableSources.push({
                    source: MainSource.MediaFromWix,
                    guid: firstSubcategoryGuid,
                    language,
                    media_type: mediaType,
                  });
                })
            : Promise.resolve());

          if (shouldLoadShutterstockImages) {
            searchableSources.push({
              source: 'shutterstock' as MainSource,
              media_type: MediaType.Picture,
              language: language?.replace(/fr|hi|no|ja|zh|uk/, 'en'),
            });
          }
          if (shouldLoadShutterstockVideos) {
            searchableSources.push({
              source: 'shutterstock-video' as MainSource,
              media_type: MediaType.Video,
              language: language?.replace(/fr|hi|no|ja|zh|uk/, 'en'),
            });
          }

          if (shouldLoadUnsplash) {
            searchableSources.push({ source: MainSource.Unsplash, ...newOptions });
          }

          fedopsLogger.interactionStarted(FedOpsInteraction.MultiSearch);
          const multiSearchAdapter = getAdapterWithAdapterSettings(MainSource.MultiSearch, settings);
          const { results, recentSearches } = await multiSearchAdapter.search.search(query, {
            sources: searchableSources,
            publicMediaCategories,
          });
          fedopsLogger.interactionEnded(FedOpsInteraction.MultiSearch);

          this.recentSearches = recentSearches;

          const resultsSortedBySource = sortObjectByKeys(results, finalSourcesOrder);
          return {
            data: chain<IServerFilesResponse, MediaManagerSDKFile>(prop('data'), values(resultsSortedBySource)),
          };
        } catch (error) {
          captureException(new Error(`[multi search]: ${error.message}`, { cause: error }), {
            tags: {
              [CustomMonitoringTag]: FedOpsInteraction.MultiSearch,
            },
          });
        }
      },
      getRecentSearches: () => this.recentSearches,
    };
  }
}
